/**
 * SessionHistory — lists the last 20 sessions for the current user.
 *
 * Fetches GET /sessions on mount with Cognito Bearer token.
 * Click row → navigate to /session/{sessionId}.
 * Trash icon → inline confirmation → DELETE /sessions/{sessionId}.
 *
 * Security (T-02-59): Cognito token required; 401 on expired session clears list.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { deleteSession } from '../../lib/session-api';
import { fetchCurriculum, type CurriculumArea } from '../../lib/curriculum-api';

interface SessionMeta {
  sessionId: string;
  mode: 'voice' | 'text';
  status: 'active' | 'ended';
  startedAt: string;
  endedAt?: string;
  turnCount: number;
  /** Auto-generated ~10-word sentence overview of the session */
  title?: string;
  /** Area IDs from post-session classifier (Phase 8) */
  areaTags?: string[];
  /** ISO timestamp of last classification (Phase 8) */
  areaTagsAt?: string;
}

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No id_token');
  return token;
}

export function SessionHistory() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [areaTitleMap, setAreaTitleMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    async function loadAreas() {
      try {
        const data = await fetchCurriculum();
        if (cancelled) return;
        const map = new Map<string, string>();
        data.areas.forEach((a: CurriculumArea) => map.set(a.id, a.title));
        setAreaTitleMap(map);
      } catch {
        // Silent failure — fall back to rendering raw IDs.
        // Session list must remain usable even if curriculum fetch fails.
      }
    }
    void loadAreas();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = await getIdToken();
        const restApiUrl = import.meta.env.VITE_REST_API_URL ?? '';
        const response = await fetch(`${restApiUrl}/sessions`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`GET /sessions returned ${response.status}`);
        }

        const body = await response.json() as { sessions: SessionMeta[] };
        if (!cancelled) {
          setSessions(body.sessions ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load sessions');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, []);

  async function handleDelete(sessionId: string) {
    setDeletingId(sessionId);
    try {
      await deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      setConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session');
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-stone-500">
        Loading sessions…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4">
        {error}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-stone-500 text-sm">No sessions yet. Start your first one above.</p>
      </div>
    );
  }

  return (
    <section aria-label="Session history">
      <h2 className="text-lg font-semibold text-stone-800 mb-3">Your Sessions</h2>
      <ul className="space-y-2" role="list">
        {sessions.map((session) => {
          const date = new Date(session.startedAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          const time = new Date(session.startedAt).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          });

          return (
            <li key={session.sessionId} className="flex items-stretch gap-2">
              {/* Main session card — navigates to session */}
              <button
                type="button"
                onClick={() => navigate(`/session/${session.sessionId}`)}
                className="flex-1 text-left px-4 py-3 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400"
                aria-label={`Session${session.title ? ` "${session.title}"` : ''} from ${date} at ${time}, ${session.mode} mode, ${session.turnCount} turns, ${session.status}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        session.mode === 'voice'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-sky-100 text-sky-700'
                      }`}
                    >
                      {session.mode === 'voice' ? '🎙️ Voice' : '⌨️ Text'}
                    </span>
                    <span className="text-sm text-stone-700 font-medium">{date}</span>
                    <span className="text-xs text-stone-500">{time}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-xs text-stone-500">
                      {session.turnCount} {session.turnCount === 1 ? 'turn' : 'turns'}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        session.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-stone-100 text-stone-500'
                      }`}
                    >
                      {session.status}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-4 h-4 text-stone-400"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                {session.title && (
                  <p className="mt-2 text-sm text-stone-600 leading-snug">
                    {session.title}
                  </p>
                )}

                {session.areaTags && session.areaTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {session.areaTags.map((tagId) => (
                      <span
                        key={tagId}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-700"
                      >
                        {areaTitleMap.get(tagId) ?? tagId}
                      </span>
                    ))}
                  </div>
                )}
              </button>

              {/* Delete control — inline two-step confirmation */}
              {confirmId === session.sessionId ? (
                <div className="flex items-center gap-1 px-2 rounded-xl border border-red-200 bg-red-50">
                  <span className="text-xs text-red-700 font-medium whitespace-nowrap">Delete?</span>
                  <button
                    type="button"
                    onClick={() => handleDelete(session.sessionId)}
                    disabled={deletingId === session.sessionId}
                    className="px-2 py-1 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {deletingId === session.sessionId ? '…' : 'Yes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    className="px-2 py-1 text-xs font-semibold text-stone-600 hover:text-stone-900 transition-colors"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmId(session.sessionId)}
                  className="px-3 rounded-xl border border-stone-200 bg-white hover:bg-red-50 hover:border-red-200 text-stone-400 hover:text-red-500 transition-all focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Delete session"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
